import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Table from '../../components/ui/Table.jsx';
import { useApi } from '../../hooks/useApi.js';
import { apiFetch } from '../../api.js';
import { RouteContext } from '../../router.jsx';

export default function StaffSurveyDetail({ params }) {
  const { navigate } = React.useContext(RouteContext);
  const surveyId = params?.id;
  const [refresh, setRefresh] = React.useState(0);
  const { data: survey, loading, error } = useApi(`/api/surveys/${surveyId}`, [surveyId, refresh]);
  const { data: results, loading: resultsLoading, error: resultsError } = useApi(`/api/surveys/${surveyId}/results`, [surveyId, refresh]);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });
  const [form, setForm] = React.useState(null);

  React.useEffect(() => {
    setMessage({ text: '', tone: 'info' });
  }, [surveyId]);

  React.useEffect(() => {
    if (!survey) return;
    setForm({
      name: survey.name || '',
      semester: survey.semester || '',
      year: survey.year || ''
    });
  }, [survey]);

  if (!surveyId) {
    return (
      <div className="page">
        <div className="alert error">Missing survey id.</div>
      </div>
    );
  }

  const handleAction = async (action) => {
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch(`/api/surveys/${surveyId}/${action}`, { method: 'POST' });
      setRefresh((value) => value + 1);
      setMessage({ text: 'Survey updated.', tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to update survey.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleMetaSave = async () => {
    if (!form) return;
    if (!form.name.trim()) {
      setMessage({ text: 'Survey name is required.', tone: 'error' });
      return;
    }
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch(`/api/surveys/${surveyId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: form.name.trim(),
          semester: form.semester.trim(),
          year: form.year.trim()
        })
      });
      setRefresh((value) => value + 1);
      setMessage({ text: 'Survey details updated.', tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to update survey.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const rows = React.useMemo(() => {
    const counts = results?.counts || {};
    return Object.entries(counts)
      .map(([course, count]) => ({ course, count }))
      .sort((a, b) => b.count - a.count);
  }, [results]);

  const columns = [
    { key: 'course', label: 'Course' },
    { key: 'count', label: 'Votes' }
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Survey Details</h1>
          <p className="subtle">Review survey status and demand results.</p>
        </div>
        <Button variant="secondary" type="button" onClick={() => navigate('/staff/surveys')}>Back to Surveys</Button>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      {loading || !survey ? (
        <p className="subtle">Loading survey...</p>
      ) : (
        <>
          <div className="grid two">
            <Card title="Survey Summary">
              <div className="summary-list">
                <div className="summary-row">
                  <span>Survey</span>
                  <span>{survey.name || '—'}</span>
                </div>
                <div className="summary-row">
                  <span>Semester</span>
                  <span>{survey.semester || '—'} {survey.year || ''}</span>
                </div>
                <div className="summary-row">
                  <span>Status</span>
                  <span>{survey.status === 1 ? <Badge tone="success">Active</Badge> : <Badge tone="info">Closed</Badge>}</span>
                </div>
              </div>
            </Card>

            <Card title="Actions">
              <p className="subtle">Only one survey can be active at a time.</p>
              <div className="form-actions">
                {survey.status === 1 ? (
                  <Button variant="secondary" type="button" disabled={saving} onClick={() => handleAction('deactivate')}>
                    Deactivate
                  </Button>
                ) : (
                  <Button type="button" disabled={saving} onClick={() => handleAction('activate')}>
                    Activate
                  </Button>
                )}
              </div>
            </Card>
          </div>

          <Card title="Edit Survey Details">
            <div className="form-grid">
              <Input label="Survey Name" value={form?.name || ''} onChange={updateField('name')} />
              <Input label="Semester" value={form?.semester || ''} onChange={updateField('semester')} placeholder="Fall" />
              <Input label="Year" value={form?.year || ''} onChange={updateField('year')} placeholder="2026" />
            </div>
            <div className="form-actions">
              <Button variant="secondary" type="button" disabled={saving} onClick={() => setForm({
                name: survey.name || '',
                semester: survey.semester || '',
                year: survey.year || ''
              })}>
                Reset
              </Button>
              <Button type="button" disabled={saving} onClick={handleMetaSave}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Card>
        </>
      )}

      <Card title="Survey Results">
        {resultsError ? <div className="alert error">{resultsError}</div> : null}
        {resultsLoading ? (
          <p className="subtle">Loading results...</p>
        ) : rows.length === 0 ? (
          <p className="subtle">No responses yet.</p>
        ) : (
          <>
            <div className="summary-list">
              <div className="summary-row">
                <span>Total Responses</span>
                <span>{results?.total_responses || 0}</span>
              </div>
            </div>
            <Table columns={columns} data={rows} />
          </>
        )}
      </Card>
    </div>
  );
}
